<template>
  <v-card variant="flat" class="google-identity-card">
    <div class="google-identity-card__header">
      <v-icon size="18" class="google-identity-card__icon">mdi-google</v-icon>
      <h2 class="google-identity-card__title">{{ $t("auth.googleAccount.title") }}</h2>
    </div>
    <p class="google-identity-card__subtitle">{{ $t("auth.googleAccount.subtitle") }}</p>

    <v-skeleton-loader v-if="loading" type="list-item-two-line" />
    <v-alert v-else-if="errorMessage" type="error" density="compact">
      {{ errorMessage }}
    </v-alert>
    <div v-else class="google-identity-card__content">
      <div class="google-identity-card__status">
        <v-chip
          :color="status.googleConnected ? 'success' : 'default'"
          :prepend-icon="status.googleConnected ? 'mdi-check-circle-outline' : 'mdi-link-off'"
        >
          {{
            status.googleConnected
              ? $t("auth.googleAccount.connected")
              : $t("auth.googleAccount.notConnected")
          }}
        </v-chip>
        <p class="google-identity-card__hint">
          {{
            status.googleConnected
              ? $t("auth.googleAccount.connectedHint")
              : $t("auth.googleAccount.connectHint")
          }}
        </p>
      </div>

      <GoogleSignInButton
        v-if="!status.googleConnected && googleClientId"
        ref="googleButton"
        :client-id="googleClientId"
        :locale="$i18n.locale"
        :disabled="submitting"
        class="google-identity-card__button"
        @credential="requestLink"
      />
      <v-alert v-if="accepted" type="info" density="compact">
        {{ $t("auth.googleAccount.checkEmail") }}
      </v-alert>
      <p v-if="status.googleConnected" class="google-identity-card__unlink-note">
        <v-icon size="15">mdi-shield-lock-outline</v-icon>
        {{ $t("auth.googleAccount.noSelfUnlink") }}
      </p>
    </div>
  </v-card>
</template>

<script>
import axios from "axios";
import GoogleSignInButton from "@/components/auth/GoogleSignInButton.vue";

export default {
  name: "GoogleIdentityCard",
  components: { GoogleSignInButton },
  data() {
    return {
      loading: true,
      submitting: false,
      accepted: false,
      errorMessage: "",
      googleClientId: "",
      status: { googleConnected: false, passwordAvailable: false, canUnlinkGoogle: false },
    };
  },
  mounted() {
    this.loadStatus();
  },
  methods: {
    async loadStatus() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const [statusResponse, capabilitiesResponse] = await Promise.all([
          axios.get("/auth/google/status"),
          axios.get("/auth/capabilities"),
        ]);
        this.status = { ...this.status, ...(statusResponse?.data || {}) };
        const registration = capabilitiesResponse?.data?.registration || {};
        this.googleClientId = registration.googleEnabled
          ? String(registration.googleClientId || "")
          : "";
      } catch {
        this.errorMessage = this.$t("auth.googleAccount.errors.load");
      } finally {
        this.loading = false;
      }
    },
    async requestLink({ credential, nonce }) {
      if (this.submitting) return;
      this.submitting = true;
      this.accepted = false;
      this.errorMessage = "";
      try {
        const response = await axios.post(
          "/auth/google/link/request",
          { credential, nonce, locale: this.$i18n.locale },
          { withCredentials: true },
        );
        if (response?.data?.alreadyLinked) {
          await this.loadStatus();
        } else {
          this.accepted = true;
        }
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error?.code === "google_email_mismatch"
            ? this.$t("auth.googleAccount.errors.emailMismatch")
            : this.$t("auth.googleAccount.errors.link");
        this.$nextTick(() => this.$refs.googleButton?.renderGoogleButton());
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.google-identity-card {
  padding: 18px 20px;
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  background: var(--app-content-background);
}

/* Același `section header` uppercase ca restul cardurilor din `Datele mele`. */
.google-identity-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.google-identity-card__icon {
  flex-shrink: 0;
  color: rgba(var(--v-theme-on-surface), 0.5);
}

.google-identity-card__title,
.google-identity-card__subtitle,
.google-identity-card__hint,
.google-identity-card__unlink-note {
  margin: 0;
}

.google-identity-card__title {
  color: var(--app-text-strong);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.google-identity-card__subtitle,
.google-identity-card__hint,
.google-identity-card__unlink-note {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 0.85rem;
  line-height: 1.55;
}

.google-identity-card__subtitle {
  margin-bottom: 14px;
}

.google-identity-card__content,
.google-identity-card__status {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.google-identity-card__button {
  max-width: 400px;
}

.google-identity-card__unlink-note {
  display: flex;
  align-items: center;
  gap: 7px;
}
</style>
